import type { IChatFolderListObject } from "@frontend-shared/types";
import { FolderOutput, Trash } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

interface IDeleteChatFolderDialogProps {
  folder: IChatFolderListObject;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onDelete: (moveChatsToRoot: boolean) => Promise<void>;
}

export const DeleteChatFolderDialog: React.FC<IDeleteChatFolderDialogProps> = ({
  folder,
  isOpen,
  onOpenChange,
  onDelete,
}) => {
  const { t } = useTranslation("sidebar");
  const [shouldMoveChatsToRoot, setShouldMoveChatsToRoot] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasChats = folder.chatCount > 0;

  // NOTE: Reset selection to default when dialog opens
  useEffect(() => {
    if (isOpen) {
      setShouldMoveChatsToRoot(true);
    }
  }, [isOpen]);

  const handleDelete = async (): Promise<void> => {
    setIsSubmitting(true);
    try {
      await onDelete(shouldMoveChatsToRoot);
      onOpenChange(false);
    } catch {
      // NOTE: Dialog stays open so user can retry
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-center text-2xl mb-1 [overflow-wrap:anywhere]">
            {t("chat_folder_list.delete_folder_dialog.title", {
              name: folder.name,
            })}
          </DialogTitle>
          <DialogDescription className="text-center text-base">
            {hasChats
              ? t("chat_folder_list.delete_folder_dialog.description")
              : t("chat_folder_list.delete_folder_dialog.description_empty")}
          </DialogDescription>
        </DialogHeader>
        {hasChats && (
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => setShouldMoveChatsToRoot(true)}
              className={cn(
                "flex items-center gap-3 w-full rounded-md border p-3 text-start transition-colors cursor-pointer",
                shouldMoveChatsToRoot
                  ? "border-primary bg-primary/10"
                  : "border-border hover:bg-muted/50",
              )}
            >
              <FolderOutput className="h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium">
                  {t("chat_folder_list.delete_folder_dialog.move_chats")}
                </span>
                <span className="text-xs text-muted-foreground">
                  {t(
                    "chat_folder_list.delete_folder_dialog.move_chats_description",
                  )}
                </span>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setShouldMoveChatsToRoot(false)}
              className={cn(
                "flex items-center gap-3 w-full rounded-md border p-3 text-start transition-colors cursor-pointer",
                shouldMoveChatsToRoot
                  ? "border-border hover:bg-muted/50"
                  : "border-destructive bg-destructive/10",
              )}
            >
              <Trash className="h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium">
                  {t("chat_folder_list.delete_folder_dialog.delete_chats")}
                </span>
                <span className="text-xs text-muted-foreground">
                  {t(
                    "chat_folder_list.delete_folder_dialog.delete_chats_description",
                  )}
                </span>
              </div>
            </button>
          </div>
        )}
        <DialogFooter className="flex items-center mt-2">
          <Button
            type="button"
            variant="outline"
            className="grow basis-1/2 shrink-0"
            onClick={() => onOpenChange(false)}
          >
            {t("chat_folder_list.delete_folder_dialog.cancel")}
          </Button>
          <Button
            variant="destructive"
            className="grow basis-1/2 shrink-0"
            onClick={handleDelete}
            disabled={isSubmitting}
          >
            {t("chat_folder_list.delete_folder_dialog.delete")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
